import type { ComponentProps, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, chakra, Flex, MenuItem, Text } from '@chakra-ui/react';
import type { CustomIconProps } from 'icon';

import { Dot } from '../Dot/Dot';
import { Link } from '../Link/Link';

interface Props extends ComponentProps<typeof Link> {
  label: string;
  icon: ReactNode;
  type?: 'mobile' | 'menuItem' | 'navbarItem';
  badge?: number;
}

const NavigationItem = ({
  label,
  to = '#',
  icon,
  type = 'navbarItem',
  badge = 0,
  ...rest
}: Props) => {
  const { pathname } = useLocation();
  const isActive = pathname === to || pathname.includes(`${to}/`);
  const shouldDisplayBadge = badge > 0;

  const iconWithColors = isValidElement<CustomIconProps>(icon)
    ? cloneElement(icon, {
        color: isActive ? 'primary200' : 'neutral200',
        secondaryColor: isActive ? 'primary500' : 'neutral400',
        solid: isActive || type !== 'navbarItem',
      })
    : undefined;

  if (type === 'menuItem') {
    return (
      <Link to={to} variant='heading6' activeStyle={{ color: 'primary500' }} {...rest}>
        <MenuItem icon={iconWithColors}>{label}</MenuItem>
      </Link>
    );
  }

  if (type === 'mobile') {
    return (
      <Link to={to} variant='heading6' activeStyle={{ color: 'primary500' }} {...rest}>
        <Flex align='center'>
          {iconWithColors}
          <Box as='span' ml={4}>
            {label}
          </Box>
        </Flex>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      display='flex'
      flexDirection='column'
      py={1}
      mt={0}
      justifyContent='center'
      alignItems='center'
      textAlign='center'
      gap={1}
      color='neutral400'
      activeStyle={{
        color: 'primary500',
        borderLeftColor: 'primary400',
      }}
      pos='relative'
      borderWidth='4px'
      borderColor='transparent'
      _hover={{
        transition: 'all 0.2s ease-in-out',
        color: 'primary500',
        borderLeftColor: 'primary400',
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
      }}
      tabIndex={0}
      title={label}
      {...rest}>
      <Dot
        variant='secondary'
        display={shouldDisplayBadge ? 'flex' : 'none'}
        px={1}
        pos='absolute'
        right={badge > 9 ? '30%' : '35%'}
        bottom='45%'>
        <Text variant='badge'>{badge > 99 ? '99+' : badge}</Text>
      </Dot>
      {iconWithColors}
      <Text noOfLines={1} maxW='100%' variant='smallSemibold'>
        {label}
      </Text>
    </Link>
  );
};

export default chakra(NavigationItem);
